import { RJSFSchema, UiSchema } from '@rjsf/utils';
import { JsonPlaceholderReplacer } from 'json-placeholder-replacer';
import { parseJson } from 'src/utilities/ApiHelper';
import {
  SchemaAttributes,
  SchemaTranslator,
} from 'src/integration/ddm/DDMSchemaTranslationProvider';
import { CaseCreationStaticSchema } from 'src/components/Forms/CreateCase/CreateCaseSchema';

export class DSAOOCSSchemaTranslator implements SchemaTranslator {
  private coreSchema: RJSFSchema;
  private uiSchema: UiSchema;
  private readonly caseSummarySchema: RJSFSchema;
  private createCaseStaticSchema: CaseCreationStaticSchema;
  private bundles: any;

  constructor(schemaAttributes: SchemaAttributes) {
    this.coreSchema = schemaAttributes.coreSchema;
    this.uiSchema = schemaAttributes.uiSchema;
    this.caseSummarySchema = schemaAttributes.caseSummarySchema;
    this.createCaseStaticSchema = schemaAttributes.createCaseStaticSchema;
    this.bundles = schemaAttributes.bundles;
  }

  translateCoreSchema(): RJSFSchema {
    return this.getPlaceholderReplacedCoreSchema(
      this.coreSchema,
      this.bundles.dsaOutOfCourtSettlementBundle,
    );
  }
  translateUiSchema(): UiSchema {
    return this.getPlaceholderReplacedUiSchema(
      this.uiSchema,
      this.bundles.dsaOutOfCourtSettlementBundle,
    );
  }
  translateCreateCaseStaticSchema(): CaseCreationStaticSchema {
    return this.getPlaceholderReplacedCreateCaseStaticSchema(
      this.createCaseStaticSchema,
      this.bundles.dsaOutOfCourtSettlementBundle,
    );
  }

  translateCaseSummarySchema(): RJSFSchema {
    return this.caseSummarySchema;
  }

  private getPlaceholderReplacedCoreSchema(
    schema: RJSFSchema,
    bundle: any,
  ): RJSFSchema {
    const placeHolderReplacer = new JsonPlaceholderReplacer();
    placeHolderReplacer.addVariableMap({
      request_title_field_name: bundle.getMessage('request_title_field_name'),
      request_summary_section_heading: bundle.getMessage(
        'request_summary_section_heading',
      ),
      oocsb_name_field_name: bundle.getMessage('oocsb_name_field_name'),
      store_field_name: bundle.getMessage('store_field_name'),
      store_field_description: bundle.getMessage('store_field_description'),
      submitter_type_field_name: bundle.getMessage('submitter_type_field_name'),
      submitter_type_option_seller: bundle.getMessage(
        'submitter_type_option_seller',
      ),
      submitter_type_option_advertiser: bundle.getMessage(
        'submitter_type_option_advertiser',
      ),
      submitter_type_option_buyer: bundle.getMessage(
        'submitter_type_option_buyer',
      ),
      submitter_type_option_brand: bundle.getMessage(
        'submitter_type_option_brand',
      ),
      name_of_submitter_field_name: bundle.getMessage(
        'name_of_submitter_field_name',
      ),
      subject_of_dispute_field_name: bundle.getMessage(
        'subject_of_dispute_field_name',
      ),
      subject_of_dispute_field_description: bundle.getMessage(
        'subject_of_dispute_field_description',
      ),
      subject_of_dispute_option_notice: bundle.getMessage(
        'subject_of_dispute_option_notice',
      ),
      subject_of_dispute_option_review: bundle.getMessage(
        'subject_of_dispute_option_review',
      ),
      subject_of_dispute_option_advert: bundle.getMessage(
        'subject_of_dispute_option_advert',
      ),
      subject_of_dispute_option_other: bundle.getMessage(
        'subject_of_dispute_option_other',
      ),
      notice_type_field_name: bundle.getMessage('notice_type_field_name'),
      notice_type_field_description: bundle.getMessage(
        'notice_type_field_description',
      ),
      notice_type_option_notice_of_infringement: bundle.getMessage(
        'notice_type_option_notice_of_infringement',
      ),
      notice_type_option_product_safety: bundle.getMessage(
        'notice_type_option_product_safety',
      ),
      notice_type_option_restricted_illegal_product: bundle.getMessage(
        'notice_type_option_restricted_illegal_product',
      ),
      asin_field_name: bundle.getMessage('asin_field_name'),
      asin_field_description: bundle.getMessage('asin_field_description'),
      previous_cids_field_name: bundle.getMessage('previous_cids_field_name'),
      previous_cids_field_description: bundle.getMessage(
        'previous_cids_field_description',
      ),
      claim_summary_field_name: bundle.getMessage('claim_summary_field_name'),
      claim_summary_field_description: bundle.getMessage(
        'claim_summary_field_description',
      ),
      previous_submission_id_field_name: bundle.getMessage(
        'previous_submission_id_field_name',
      ),
      previous_submission_id_field_description: bundle.getMessage(
        'previous_submission_id_field_description',
      ),
      mandatory_attachment_field_name: bundle.getMessage(
        'mandatory_attachment_field_name',
      ),
      mandatory_attachment_field_description: bundle.getMessage(
        'mandatory_attachment_field_description',
      ),
    });

    return placeHolderReplacer.replace(parseJson(JSON.stringify(schema)));
  }

  private getPlaceholderReplacedUiSchema(
    schema: UiSchema,
    bundle: any,
  ): UiSchema {
    const placeHolderReplacer = new JsonPlaceholderReplacer();

    placeHolderReplacer.addVariableMap({
      mandatory_field_error_message: bundle.getMessage(
        'mandatory_field_error_message',
      ),
      number_field_error_message: bundle.getMessage(
        'number_field_error_message',
      ),
      max_length_error_message: bundle.getMessage('max_length_error_message'),
    });

    return placeHolderReplacer.replace(parseJson(JSON.stringify(schema)));
  }

  private getPlaceholderReplacedCreateCaseStaticSchema(
    schema: CaseCreationStaticSchema,
    bundle: any,
  ): CaseCreationStaticSchema {
    const placeHolderReplacer = new JsonPlaceholderReplacer();

    placeHolderReplacer.addVariableMap({
      referral_information_section_header: bundle.getMessage(
        'referral_information_section_header',
      ),
      referral_information_full_name_field_name: bundle.getMessage(
        'referral_information_full_name_field_name',
      ),
      referral_information_email_field_name: bundle.getMessage(
        'referral_information_email_field_name',
      ),
      referral_information_agency_field_name: bundle.getMessage(
        'referral_information_agency_field_name',
      ),
      referral_information_country_field_name: bundle.getMessage(
        'referral_information_country_field_name',
      ),
      referral_information_role_field_name: bundle.getMessage(
        'referral_information_role_field_name',
      ),
    });

    return placeHolderReplacer.replace(
      parseJson(JSON.stringify(schema)),
    ) as CaseCreationStaticSchema;
  }
}
